import React, { useState, useEffect } from 'react';
import { AlertCircle, Edit2, Save, X } from 'lucide-react';
import axios from 'axios';
import Header from "../../../components/Header/Header";
import Footer from "../../../components/Footer/Footer";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

const StoreOfficerProjectAssignment = () => {
  const [storeOfficers, setStoreOfficers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [assignedProjects, setAssignedProjects] = useState([]);
  const [selectedStoreOfficer, setSelectedStoreOfficer] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingProject, setEditingProject] = useState(null);
  const [editingOfficer, setEditingOfficer] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);

      const [officersResponse, projectsResponse, assignedResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/storeoff/users`),
        axios.get(`${API_BASE_URL}/storeoff/projects`),
        axios.get(`${API_BASE_URL}/storeoff/projects/assigned`)
      ]);

      const storeOfficersData = officersResponse.data.filter(
        (user) => (user.Role === "Store Officer" || user.Role === "PE&SO") && user.IsAuthorized
      );
      setStoreOfficers(storeOfficersData);
      setProjects(projectsResponse.data);
      setAssignedProjects(assignedResponse.data);

      setLoading(false);
    } catch (err) {
      setError("Failed to load data");
      console.error(err);
      setLoading(false);
    }
  };

  const handleAssignProject = async () => {
    if (!selectedProject || !selectedStoreOfficer) {
      setError("Please select both a store officer and a project");
      return;
    }

    try {
      await axios.post(`${API_BASE_URL}/storeoff/project-team`, {
        ProjectID: selectedProject,
        SOIDs: [selectedStoreOfficer], // Send as array for consistency with backend
      });

      await fetchData();
      setSelectedStoreOfficer(null);
      setSelectedProject(null);
      setError(null);
      alert("Project assigned successfully!");
    } catch (err) {
      setError("Failed to assign project");
      console.error(err);
    }
  };

  const handleEditProject = (project) => {
    setEditingProject(project);
    setEditingOfficer(null);
  };

  const handleSaveProjectOfficer = async () => {
    if (!editingOfficer) {
      setError("Please select a store officer");
      return;
    }

    try {
      // Get current store officers
      const currentOfficers = editingProject.storeOfficers.map(so => so.SOID);
      // Add new officer if not already assigned
      if (!currentOfficers.includes(editingOfficer)) {
        await axios.post(`${API_BASE_URL}/storeoff/project-team`, {
          ProjectID: editingProject.ProjectID,
          SOIDs: [editingOfficer]
        });
        await fetchData();
        setEditingProject(null);
        setEditingOfficer(null);
        alert("Store officer added successfully!");
      } else {
        setError("Store officer is already assigned to this project");
      }
    } catch (err) {
      setError("Failed to add store officer");
      console.error(err);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <Header />

      <main className="flex-grow p-4">
        <div className="max-w-6xl mx-auto p-4 border rounded-lg shadow-md bg-white">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold">Store Officer Project Assignment</h2>
            <AlertCircle className="w-6 h-6 text-blue-500" />
          </div>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
              <span className="block sm:inline">{error}</span>
            </div>
          )}

          {/* Assignment Form */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block mb-2 text-sm font-medium">Select Store Officer</label>
              <select
                value={selectedStoreOfficer || ""}
                onChange={(e) => {
                  setSelectedStoreOfficer(Number(e.target.value));
                  setError(null);
                }}
                className="w-full p-2 border rounded"
              >
                <option value="">Choose Store Officer</option>
                {storeOfficers.map((officer) => (
                  <option key={officer.UserID} value={officer.UserID}>
                    {officer.Username} ({officer.Email})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block mb-2 text-sm font-medium">Select Project</label>
              <select
                value={selectedProject || ""}
                onChange={(e) => {
                  setSelectedProject(Number(e.target.value));
                  setError(null);
                }}
                className="w-full p-2 border rounded"
              >
                <option value="">Choose Project</option>
                {projects.map((project) => (
                  <option key={project.ProjectID} value={project.ProjectID}>
                    {project.ProjectName} ({project.ProjectStage})
                  </option>
                ))}
              </select>
            </div>
          </div>

          <button
            onClick={handleAssignProject}
            disabled={!selectedProject || !selectedStoreOfficer}
            className="w-full p-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300"
          >
            Assign Project
          </button>

          {/* Assigned Projects Section */}
          <div className="mt-8">
            <h3 className="text-lg font-semibold mb-4">Assigned Projects</h3>
            {assignedProjects.length === 0 ? (
              <p className="text-gray-500 text-center">No assigned projects</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border p-2 text-left">Project Name</th>
                      <th className="border p-2 text-left">Project Stage</th>
                      <th className="border p-2 text-left">Store Officers</th>
                      <th className="border p-2 text-left">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {assignedProjects.map((project) => (
                      <tr key={project.ProjectID} className="hover:bg-gray-50">
                        <td className="border p-2">{project.ProjectName}</td>
                        <td className="border p-2">{project.ProjectStage}</td>
                        <td className="border p-2">
                          {editingProject?.ProjectID === project.ProjectID ? (
                            <select
                              value={editingOfficer || ""}
                              onChange={(e) => setEditingOfficer(Number(e.target.value))}
                              className="w-full p-2 border rounded"
                            >
                              <option value="">Select Store Officer</option>
                              {storeOfficers.map((officer) => (
                                <option key={officer.UserID} value={officer.UserID}>
                                  {officer.Username}
                                </option>
                              ))}
                            </select>
                          ) : (
                            project.storeOfficers.map(so => so.Username).join(", ")
                          )}
                        </td>
                        <td className="border p-2">
                          {editingProject?.ProjectID === project.ProjectID ? (
                            <div className="flex space-x-2">
                              <button
                                onClick={handleSaveProjectOfficer}
                                className="p-1 text-green-600 hover:text-green-800"
                              >
                                <Save className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => setEditingProject(null)}
                                className="p-1 text-red-600 hover:text-red-800"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => handleEditProject(project)}
                              className="p-1 text-blue-600 hover:text-blue-800"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default StoreOfficerProjectAssignment;